/**
 * Guest checkout helpers (receipt email on order billing only).
 */

export function isGuestCheckoutEmailConfigured() {
  return !!(process.env.REACT_APP_GUEST_CHECKOUT_EMAIL || '').trim();
}

export function getGuestPlaceholderEmail() {
  return (process.env.REACT_APP_GUEST_CHECKOUT_EMAIL || '').trim().toLowerCase();
}

/**
 * Strict: no whitespace anywhere in the address.
 * A loose /.+@.+\..+/ accepted values like "user@gmail .com" (note the space). Stripe rejects
 * those when the address is passed as receipt_email, which fails PaymentIntent creation and so
 * kills the whole payment rather than just skipping the receipt.
 */
export const RECEIPT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidReceiptEmail(email) {
  const t = (email || '').trim();
  if (!RECEIPT_EMAIL_RE.test(t)) return false;
  const placeholder = getGuestPlaceholderEmail();
  if (placeholder && t.toLowerCase() === placeholder) return false;
  return true;
}

/** Specific, fixable message for why an address was rejected. */
export function getReceiptEmailError(email) {
  const t = (email || '').trim();
  if (!t) return 'Enter your email address to receive your receipt.';
  if (/\s/.test(t)) return `Email addresses can't contain spaces. Please check "${t}" and try again.`;
  if (!t.includes('@')) return `"${t}" is missing an @. Enter an address like name@example.com.`;
  if (!/\.[^\s@]+$/.test(t)) return `"${t}" is missing the ending, like .com. Enter an address like name@example.com.`;
  const placeholder = getGuestPlaceholderEmail();
  if (placeholder && t.toLowerCase() === placeholder) {
    return 'Enter your own email address so we can send your receipt.';
  }
  return `"${t}" doesn't look like a valid email. Enter an address like name@example.com.`;
}

export function isValidReceiptName(name) {
  return typeof name === 'string' && name.trim().length > 0;
}

/**
 * Build customer payload for pending CC / Zelle orders (guest path only).
 */
export function buildGuestOrderCustomer(sessionUser, { wantsReceipt, receiptEmail, firstName, lastName } = {}) {
  const customerId =
    sessionUser?.customer_id ||
    sessionUser?.id ||
    process.env.REACT_APP_GUEST_CHECKOUT_USER_ID;

  const billing = { ...(sessionUser?.billing || {}) };

  if (wantsReceipt) {
    if (isValidReceiptEmail(receiptEmail)) {
      billing.email = receiptEmail.trim();
    }
    if (isValidReceiptName(firstName)) {
      billing.first_name = firstName.trim();
    }
    if (isValidReceiptName(lastName)) {
      billing.last_name = lastName.trim();
    }
  }

  const receiptEmailOk = wantsReceipt && isValidReceiptEmail(receiptEmail);
  const firstOk = wantsReceipt && isValidReceiptName(firstName);
  const lastOk = wantsReceipt && isValidReceiptName(lastName);

  return {
    ...sessionUser,
    customer_id: customerId,
    first_name: firstOk ? firstName.trim() : sessionUser?.first_name,
    last_name: lastOk ? lastName.trim() : sessionUser?.last_name,
    billing,
    isGuestCheckout: true,
    guestReceiptOptIn: !!wantsReceipt,
    guestReceiptEmail: receiptEmailOk ? receiptEmail.trim() : undefined,
    guestReceiptFirstName: firstOk ? firstName.trim() : undefined,
    guestReceiptLastName: lastOk ? lastName.trim() : undefined
  };
}

export function validateGuestReceiptInput(isGuest, wantsReceipt, { email, firstName, lastName } = {}) {
  if (!isGuest || !wantsReceipt) return { ok: true };
  if (!isValidReceiptName(firstName) || !isValidReceiptName(lastName)) {
    return {
      ok: false,
      message: 'Enter your first and last name for the receipt.'
    };
  }
  if (!isValidReceiptEmail(email)) {
    return {
      ok: false,
      message: getReceiptEmailError(email)
    };
  }
  return { ok: true };
}

/** Stripe Terminal PaymentIntent fields when guest opted in for receipt. */
export function getGuestStripeReceiptFields(customer) {
  if (!customer?.guestReceiptOptIn) return {};
  const email = customer?.billing?.email || customer?.guestReceiptEmail;
  const first = customer?.billing?.first_name || customer?.guestReceiptFirstName;
  const last = customer?.billing?.last_name || customer?.guestReceiptLastName;
  if (!isValidReceiptEmail(email) || !isValidReceiptName(first) || !isValidReceiptName(last)) {
    return {};
  }
  return {
    receipt_email: email.trim(),
    billing_first_name: first.trim(),
    billing_last_name: last.trim()
  };
}
